import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  UserSliceInitalState,
  APIResponse,
  ListDataRequestPayload,
  GetPatientsSuccessResponse,
  GetSpecialistsSuccessResponse,
  GetAdminsRequestPayload,
  GetAdminsSuccessResponse,
  AddAdminRequestPayload,
  AddAdminSuccessResponse,
  DeleteAdminRequestPayload,
  DeleteAdminSuccessResponse,
  UpdateAdminRequestPayload,
  UpdateAdminSuccessResponse,
  UpdateUserRecordPayload,
  GetAuditLogsRequestPayload,
  GetAuditLogsSuccessResponse,
  GetAccessControlRolesSuccessResponse,
  GetAccessControlAccessModulesSuccessResponse,
  GetAccessControlAccessModulesRequestPayload,
  UpdateRoleRequestPayload,
  UpdateRoleSuccessResponse,
  ToggleLicenceVerificationRequestPayload,
  ToggleLicenceVerificationSuccessResponse,
  GetLicenceVerificationModeSuccessResponse,
  GetAllAccessModulesSuccessResponse,
} from '@/interface';
import {LicenceVerificationMode} from '@/constants';

const initialState: UserSliceInitalState = {
  isLoading: false,
  superAdminDashboardSuccess: false,
  superAdminDashboardFailure: false,
  manageUsers: {
    users: [],
    userTab: 'specialist',
    currentPage: 0,
    totalPages: 0,
    totalResults: 0,
    nextPage: 0,
  },
  adminManagement: {
    admins: [],
    currentPage: 0,
    totalPages: 0,
    totalResults: 0,
    nextPage: null,
  },
  accessControl: {
    currentPage: 0,
    totalPages: 0,
    totalResults: 0,
    nextPage: null,
    roles: [],
    accessModules: [],
  },
  licenceVerification: {
    mode: LicenceVerificationMode.Automatic,
    lastUpdated: null,
  },
  // Audit Logs state
  auditLogs: [],
  auditLogsCurrentPage: 0,
  auditLogsTotalPages: 0,
  auditLogsTotalResults: 0,
  auditLogsNextPage: null,
  auditLogsSuccess: false,
  auditLogsFailure: false,
  // Access Modules List
  accessModulesList: [],
  accessModulesListSuccess: false,
  accessModulesListFailure: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    /**
     * get patients list
     */
    getPatientsRequest: (state, action: PayloadAction<ListDataRequestPayload>) => {
      state.isLoading = true;
      state.superAdminDashboardFailure = false;
      state.superAdminDashboardSuccess = false;
    },
    getPatientsSuccess: (state, action: PayloadAction<GetPatientsSuccessResponse>) => {
      state.isLoading = false;
      state.manageUsers.users = action.payload.data.patients;
      state.manageUsers.userTab = 'patient';
      if (action.payload.data.patients.length === 0) {
        state.superAdminDashboardFailure = 'E-10044';
        state.manageUsers.totalPages = 0;
        state.manageUsers.totalResults = 0;
        state.manageUsers.currentPage = 0;
        state.manageUsers.nextPage = 0;
      } else {
        state.superAdminDashboardSuccess = action.payload.status_code;
        state.manageUsers.totalPages = action.payload.data.total_page;
        state.manageUsers.totalResults = action.payload.data.total_result;
        state.manageUsers.currentPage = action.payload.data.current_page;
        state.manageUsers.nextPage = action.payload.data.next_page;
      }
    },
    getPatientsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardFailure = action.payload.status_code;
      if (action.payload.status_code === 'E-10044') {
        state.manageUsers.users = [];
        state.manageUsers.totalPages = 0;
        state.manageUsers.totalResults = 0;
        state.manageUsers.currentPage = 0;
        state.manageUsers.nextPage = 0;
      }
    },

    /**
     * get specialist list
     */
    getSpecialistsRequest: (state, action: PayloadAction<ListDataRequestPayload>) => {
      state.isLoading = true;
      state.superAdminDashboardSuccess = false;
      state.superAdminDashboardFailure = false;
    },
    getSpecialistsSuccess: (state, action: PayloadAction<GetSpecialistsSuccessResponse>) => {
      state.isLoading = false;
      state.manageUsers.users = action.payload.data.specialists;
      state.manageUsers.userTab = 'specialist';
      if (action.payload.data.specialists.length === 0) {
        state.superAdminDashboardFailure = 'E-10044';
        state.manageUsers.totalPages = 0;
        state.manageUsers.totalResults = 0;
        state.manageUsers.currentPage = 0;
        state.manageUsers.nextPage = 0;
      } else {
        state.superAdminDashboardSuccess = action.payload.status_code;
        state.manageUsers.totalPages = action.payload.data.total_page;
        state.manageUsers.totalResults = action.payload.data.total_result;
        state.manageUsers.currentPage = action.payload.data.current_page;
        state.manageUsers.nextPage = action.payload.data.next_page;
      }
    },
    getSpecialistsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardFailure = action.payload.status_code;
      if (action.payload.status_code === 'E-10044') {
        state.manageUsers.users = [];
        state.manageUsers.totalPages = 0;
        state.manageUsers.totalResults = 0;
        state.manageUsers.currentPage = 0;
        state.manageUsers.nextPage = 0;
      }
    },

    /**
     * update user record
     */
    updateUserRecordRequest(state, action: PayloadAction<UpdateUserRecordPayload>) {
      state.isLoading = true;
      state.superAdminDashboardSuccess = false;
      state.superAdminDashboardFailure = false;
    },
    updateUserRecordFailure(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.superAdminDashboardFailure = action.payload.status_code;
    },
    updateUserRecordSuccess(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.superAdminDashboardSuccess = action.payload.status_code;
    },

    updateUserRecordRealTimeSuccess(state, action: PayloadAction<APIResponse>) {
      state.isLoading = false;
      state.superAdminDashboardSuccess = action.payload.status_code;
    },

    /**
     * get admins list
     */
    getAdminsRequest: (state, action: PayloadAction<GetAdminsRequestPayload>) => {
      state.isLoading = true;
      state.superAdminDashboardSuccess = false;
      state.superAdminDashboardFailure = false;
    },
    getAdminsSuccess: (state, action: PayloadAction<GetAdminsSuccessResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardSuccess = action.payload.status_code;
      state.adminManagement.admins = action.payload.data.admins;
      state.adminManagement.totalPages = action.payload.data.total_pages;
      state.adminManagement.totalResults = action.payload.data.total_results;
      state.adminManagement.currentPage = action.payload.data.current_page;
      state.adminManagement.nextPage = action.payload.data.next_page;
    },
    getAdminsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardFailure = action.payload.status_code;
      if (['E-10054', 'E-10044'].includes(action.payload.status_code)) {
        state.adminManagement.admins = [];
        state.adminManagement.totalPages = 0;
        state.adminManagement.totalResults = 0;
        state.adminManagement.currentPage = 0;
        state.adminManagement.nextPage = null;
      }
    },

    /**
     * add admin
     */
    addAdminRequest: (state, action: PayloadAction<AddAdminRequestPayload>) => {
      state.isLoading = true;
      state.superAdminDashboardSuccess = false;
      state.superAdminDashboardFailure = false;
    },
    addAdminSuccess: (state, action: PayloadAction<AddAdminSuccessResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardSuccess = action.payload.status_code;
    },
    addAdminFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * delete admin
     */
    deleteAdminRequest: (state, action: PayloadAction<DeleteAdminRequestPayload>) => {
      state.isLoading = true;
      state.superAdminDashboardSuccess = false;
      state.superAdminDashboardFailure = false;
    },
    deleteAdminSuccess: (state, action: PayloadAction<DeleteAdminSuccessResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardSuccess = action.payload.status_code;
    },
    deleteAdminFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardFailure = action.payload.status_code;
    },
    /**
     * update admin
     */
    updateAdminRequest: (state, action: PayloadAction<UpdateAdminRequestPayload>) => {
      state.isLoading = true;
      state.superAdminDashboardSuccess = false;
      state.superAdminDashboardFailure = false;
    },
    updateAdminSuccess: (state, action: PayloadAction<UpdateAdminSuccessResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardSuccess = action.payload.status_code;
    },
    updateAdminFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardFailure = action.payload.status_code;
    },

    /*
     * get access control roles
     */
    getAccessControlRolesRequest: (state, action: PayloadAction<ListDataRequestPayload>) => {
      state.isLoading = true;
      state.superAdminDashboardSuccess = false;
      state.superAdminDashboardFailure = false;
    },

    getAccessControlRolesSuccess: (state, action: PayloadAction<GetAccessControlRolesSuccessResponse>) => {
      state.isLoading = false;
      state.accessControl.roles = action.payload.data.roles;
      state.accessControl.currentPage = action.payload.data.current_page;
      state.accessControl.totalPages = action.payload.data.total_pages;
      state.accessControl.totalResults = action.payload.data.total_results;
      state.accessControl.nextPage = action.payload.data.next_page;
      state.superAdminDashboardSuccess = action.payload.status_code;
    },
    getAccessControlRolesFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardFailure = action.payload.status_code;
    },
    /*
     * get access control access modules
     */
    getAccessControlAccessModulesRequest: (state, action: PayloadAction<GetAccessControlAccessModulesRequestPayload>) => {
      state.isLoading = true;
      state.superAdminDashboardSuccess = false;
      state.superAdminDashboardFailure = false;
    },
    getAccessControlAccessModulesSuccess: (state, action: PayloadAction<GetAccessControlAccessModulesSuccessResponse>) => {
      state.isLoading = false;
      state.accessControl.accessModules = action.payload.data.access_modules;
      state.accessControl.currentPage = action.payload.data.current_page;
      state.accessControl.totalPages = action.payload.data.total_pages;
      state.accessControl.totalResults = action.payload.data.total_results;
      state.accessControl.nextPage = action.payload.data.next_page;
      state.superAdminDashboardSuccess = action.payload.status_code;
    },
    getAccessControlAccessModulesFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * update role
     */
    updateRoleRequest: (state, action: PayloadAction<UpdateRoleRequestPayload>) => {
      state.isLoading = true;
      state.superAdminDashboardSuccess = false;
      state.superAdminDashboardFailure = false;
    },
    updateRoleSuccess: (state, action: PayloadAction<UpdateRoleSuccessResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardSuccess = action.payload.status_code;
      state.accessControl.roles = state.accessControl.roles.map(role =>
        role.id === action.payload.data.id
          ? {
              ...role,
              name: action.payload.data.name,
            }
          : role,
      );
    },

    updateRoleFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * get licence verification mode
     */
    getLicenceVerificationModeRequest: (state) => {
      state.isLoading = true;
      state.superAdminDashboardSuccess = false;
      state.superAdminDashboardFailure = false;
    },
    getLicenceVerificationModeSuccess: (state, action: PayloadAction<GetLicenceVerificationModeSuccessResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardSuccess = action.payload.status_code;
      // Ensure licenceVerification exists (for persisted state compatibility)
      if (!state.licenceVerification) {
        state.licenceVerification = {
          mode: LicenceVerificationMode.Automatic,
          lastUpdated: null,
        };
      }
      state.licenceVerification.mode = action.payload.data.mode;
      state.licenceVerification.lastUpdated = action.payload.data.last_updated;
    },
    getLicenceVerificationModeFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * toggle licence verification
     */
    toggleLicenceVerificationRequest: (state, action: PayloadAction<ToggleLicenceVerificationRequestPayload>) => {
      state.isLoading = true;
      state.superAdminDashboardSuccess = false;
      state.superAdminDashboardFailure = false;
    },
    toggleLicenceVerificationSuccess: (state, action: PayloadAction<ToggleLicenceVerificationSuccessResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardSuccess = action.payload.status_code;
      // Ensure licenceVerification exists (for persisted state compatibility)
      if (!state.licenceVerification) {
        state.licenceVerification = {
          mode: LicenceVerificationMode.Automatic,
          lastUpdated: null,
        };
      }
      state.licenceVerification.mode = action.payload.data.mode;
      state.licenceVerification.lastUpdated = action.payload.data.last_updated;
    },
    toggleLicenceVerificationFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.superAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * get audit logs
     */
    getAuditLogsRequest: (state, action: PayloadAction<GetAuditLogsRequestPayload>) => {
      state.isLoading = true;
      state.auditLogsSuccess = false;
      state.auditLogsFailure = false;
    },
    getAuditLogsSuccess: (state, action: PayloadAction<GetAuditLogsSuccessResponse>) => {
      state.isLoading = false;
      state.auditLogs = action.payload.data.logs;
      if (action.payload.data.logs.length === 0) {
        state.auditLogsFailure = 'E-10044';
        state.auditLogsTotalPages = 0;
        state.auditLogsTotalResults = 0;
        state.auditLogsCurrentPage = 0;
        state.auditLogsNextPage = null;
      } else {
        state.auditLogsSuccess = action.payload.status_code;
        state.auditLogsTotalPages = action.payload.data.total_pages;
        state.auditLogsTotalResults = action.payload.data.total_results;
        state.auditLogsCurrentPage = action.payload.data.current_page;
        state.auditLogsNextPage = action.payload.data.next_page;
      }
    },
    getAuditLogsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.auditLogsFailure = action.payload.status_code;
      if (action.payload.status_code === 'E-10044') {
        state.auditLogs = [];
        state.auditLogsTotalPages = 0;
        state.auditLogsTotalResults = 0;
        state.auditLogsCurrentPage = 0;
        state.auditLogsNextPage = null;
      }
    },
    resetAuditLogsMessage: state => {
      state.auditLogsFailure = false;
      state.auditLogsSuccess = false;
    },

    /**
     * get access modules list
     */
    getAccessModulesListRequest: (state) => {
      state.accessModulesListSuccess = false;
      state.accessModulesListFailure = false;
    },
    getAccessModulesListSuccess: (state, action: PayloadAction<GetAllAccessModulesSuccessResponse>) => {
      if (action.payload.data?.access_modules) {
        state.accessModulesList = action.payload.data.access_modules;
        state.accessModulesListSuccess = action.payload.status_code;
      } else {
        state.accessModulesListFailure = 'E-10001';
      }
    },
    getAccessModulesListFailure: (state, action: PayloadAction<APIResponse>) => {
      state.accessModulesListFailure = action.payload.status_code;
      state.accessModulesList = [];
    },

    /**
     * cancel and reset reducers
     */
    cancelUserRequest(state) {
      state.isLoading = false;
      state.manageUsers.userTab = 'specialist';
    },
    resetUserMessage: state => {
      state.superAdminDashboardFailure = false;
      state.superAdminDashboardSuccess = false;
    },
  },
});

export const {
  // patient list api
  getPatientsFailure,
  getPatientsRequest,
  getPatientsSuccess,

  // specialist list api
  getSpecialistsFailure,
  getSpecialistsRequest,
  getSpecialistsSuccess,

  // admin list api
  getAdminsFailure,
  getAdminsRequest,
  getAdminsSuccess,

  // add admin api
  addAdminFailure,
  addAdminRequest,
  addAdminSuccess,

  // delete admin api
  deleteAdminFailure,
  deleteAdminRequest,
  deleteAdminSuccess,

  // update admin api
  updateAdminFailure,
  updateAdminRequest,
  updateAdminSuccess,

  // update user api
  updateUserRecordFailure,
  updateUserRecordRequest,
  updateUserRecordSuccess,

  // audit logs api
  getAuditLogsRequest,
  getAuditLogsSuccess,
  getAuditLogsFailure,
  resetAuditLogsMessage,
  // get access control roles api
  getAccessControlRolesFailure,
  getAccessControlRolesRequest,
  getAccessControlRolesSuccess,

  // get access control access modules api
  getAccessControlAccessModulesFailure,
  getAccessControlAccessModulesRequest,
  getAccessControlAccessModulesSuccess,

  // update role api
  updateRoleFailure,
  updateRoleRequest,
  updateRoleSuccess,

  // get licence verification mode api
  getLicenceVerificationModeFailure,
  getLicenceVerificationModeRequest,
  getLicenceVerificationModeSuccess,

  // toggle licence verification api
  toggleLicenceVerificationFailure,
  toggleLicenceVerificationRequest,
  toggleLicenceVerificationSuccess,

  // get access modules list api
  getAccessModulesListFailure,
  getAccessModulesListRequest,
  getAccessModulesListSuccess,

  // cancel and reset reducers
  resetUserMessage,
  cancelUserRequest,
} = userSlice.actions;
export default userSlice.reducer;
